/**
 * Which business this browser tab is looking at.
 *
 * Taken from the first segment of the path — `app.monrdv.ca/hydrokleen/customers` — and fixed
 * for the life of the tab.
 *
 * This is the whole point of the slug being in the URL. The active business used to live only
 * in the session row on the server, which every tab shares: switching in one tab silently
 * re-pointed all the others, so a coordinator could be reading Hydrokleen, still see
 * "hydrokleen-laval" in the corner, and be served another company's customers. Exactly the
 * mistake the colour-coded badge exists to prevent. A tab now carries its own answer.
 *
 * The slug is a hint, never a permission. The server checks it against the signed-in person's
 * memberships on every request and answers 404 for one they do not hold — so typing another
 * company's name into the address bar reveals nothing.
 */

/** Paths that belong to the person rather than to a business, and so carry no slug. */
const UNSCOPED = ['/auth/', '/me/', '/health'];

/**
 * Reserved first segments: real application routes that must never be mistaken for a slug.
 *
 * On a URL that carries no business — `app.monrdv.ca/intake`, which is every URL in local
 * development — the first segment is all there is to go on. A route missing from this set is
 * read as a business name, and then *every* request on that screen goes to
 * `/api/intake/invoices` and 404s. The screen loads and everything on it is empty.
 *
 * Three routes were missing from this list at once (`intake`, `addresses`, `journal`), which
 * is the argument against maintaining it by hand: nothing about adding a route reminds you.
 * `assertRoutesReserved` below closes that, so the omission is a loud console error the first
 * time the application starts rather than an empty screen somebody reports days later.
 */
const RESERVED = new Set([
  '',
  'connexion',
  'board',
  'team',
  'customers',
  'jobs',
  'quotes',
  'invoices',
  'unbilled',
  'inbox',
  'intake',
  'addresses',
  'journal',
  'transcripts',
  'notices',
  // Static directories Vite serves, which are not routes but are equally not businesses.
  'assets',
  'fonts',
  'brand',
]);

/**
 * Fail loudly when a route has been added without reserving its segment.
 *
 * Called once at start-up with the router's own paths, so the two cannot drift. A warning
 * rather than a throw: a missing reservation breaks one screen, and taking the whole
 * application down over it would be a worse trade than a red console line naming the fix.
 */
export function assertRoutesReserved(paths: readonly string[]): void {
  const missing = paths
    .map((path) => path.split('/')[1] ?? '')
    .filter((segment) => segment && !segment.startsWith('$') && !RESERVED.has(segment));

  if (missing.length > 0) {
    console.error(
      `[tenant] these route segments are not reserved and will be read as a business name: ` +
        `${[...new Set(missing)].join(', ')}. Add them to RESERVED in lib/tenant.ts.`,
    );
  }
}

let current: string | null = null;

/** Read the slug out of the current path, or null when the URL does not carry one. */
export function readTenantSlug(): string | null {
  if (typeof window === 'undefined') return null;
  const first = window.location.pathname.split('/')[1] ?? '';
  if (!first || RESERVED.has(first)) return null;
  // Slugs are lowercase words and hyphens; anything else is some other kind of URL.
  return /^[a-z0-9][a-z0-9-]*$/.test(first) ? first : null;
}

export function setTenantSlug(slug: string | null): void {
  current = slug;
}

export function getTenantSlug(): string | null {
  return current ?? readTenantSlug();
}

/**
 * Turn an API path into a full one.
 *
 * Business paths gain the slug; the ones about the signed-in person do not. Done in one place
 * so no screen has to remember, and so adding an endpoint cannot quietly get it wrong.
 */
export function apiPath(path: string): string {
  if (UNSCOPED.some((prefix) => path.startsWith(prefix))) return `/api${path}`;
  const slug = getTenantSlug();
  return slug ? `/api/${slug}${path}` : `/api${path}`;
}

/** The same page in another business — used by the workspace switcher. */
export function pathInTenant(slug: string): string {
  const rest = window.location.pathname.split('/').slice(2).join('/');
  return `/${slug}/${rest}${window.location.search}`;
}
